/**
 * Alert component for canvas rendering.
 * Displays status or warning messages.
 */

import React, { memo, useState } from 'react';
import { AlertCircle, CheckCircle, Info, XCircle, X } from 'lucide-react';
import type { AlertProps as AlertPropsType } from '../../../types/canvas';

export interface AlertComponentProps extends AlertPropsType {}

function AlertComponentBase({
  message,
  severity = 'info',
  title,
  dismissible = false,
}: AlertComponentProps) {
  const [dismissed, setDismissed] = useState(false);

  if (dismissed) return null;

  const severityConfig = {
    info: {
      bg: 'bg-blue-50 dark:bg-blue-900/20',
      border: 'border-blue-200 dark:border-blue-800',
      text: 'text-blue-800 dark:text-blue-300',
      icon: Info,
      iconColor: 'text-blue-500 dark:text-blue-400',
    },
    success: {
      bg: 'bg-green-50 dark:bg-green-900/20',
      border: 'border-green-200 dark:border-green-800',
      text: 'text-green-800 dark:text-green-300',
      icon: CheckCircle,
      iconColor: 'text-green-500 dark:text-green-400',
    },
    warning: {
      bg: 'bg-yellow-50 dark:bg-yellow-900/20',
      border: 'border-yellow-200 dark:border-yellow-800',
      text: 'text-yellow-800 dark:text-yellow-300',
      icon: AlertCircle,
      iconColor: 'text-yellow-500 dark:text-yellow-400',
    },
    error: {
      bg: 'bg-red-50 dark:bg-red-900/20',
      border: 'border-red-200 dark:border-red-800',
      text: 'text-red-800 dark:text-red-300',
      icon: XCircle,
      iconColor: 'text-red-500 dark:text-red-400',
    },
  };

  const config = severityConfig[severity];
  const Icon = config.icon;

  return (
    <div className={`rounded-lg border p-4 ${config.bg} ${config.border}`}>
      <div className="flex items-start gap-3">
        <Icon size={20} className={`flex-shrink-0 mt-0.5 ${config.iconColor}`} />
        <div className="flex-1 min-w-0">
          {title && <h4 className={`font-medium mb-1 ${config.text}`}>{title}</h4>}
          <p className={`text-sm ${config.text}`}>{message}</p>
        </div>
        {dismissible && (
          <button
            onClick={() => setDismissed(true)}
            className={`flex-shrink-0 p-1 rounded hover:bg-black/5 dark:hover:bg-white/10 ${config.text}`}
            aria-label="Dismiss"
          >
            <X size={16} />
          </button>
        )}
      </div>
    </div>
  );
}

export const AlertComponent = memo(AlertComponentBase);
